import { Component, signal, input, effect, output } from '@angular/core';
import { DaypartDay, OrgUser, Ride, User, UserInRide, Vehicle } from 'src/app/interfaces';
import { doc, getDoc } from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import { valueToDate } from 'src/app/globals';
import { NgClass } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { getMapsRouteUrl } from '../../helpers/ride-helper';

@Component({
  selector: 'app-event-item',
  templateUrl: './event-item.component.html',
  styleUrl: './event-item.component.scss',
  imports: [NgClass, MatButton],
})
export class EventItemComponent {
  ride = input<Ride | undefined>(undefined);
  vehicle = input<Vehicle | undefined>(undefined);
  dayPartDay = input<DaypartDay | undefined>(undefined);
  cssClasses = input('');
  type = input('planning');
  driver = input<User | undefined>(undefined);
  coDriver = input<User | undefined>(undefined);

  itemClicked = output<void>();

  driverName = signal<string | null>(null);
  coDriverName = signal<string | null>(null);
  rideUsers = signal<UserInRide[]>([]);

  dataStore = dataStore;

  constructor() {
    effect(async () => {
      const ride = this.ride();
      const dayPartDay = this.dayPartDay();

      this.rideUsers.set(Object.values(ride?.users || {}));
      this.driverName.set(dayPartDay?.driverName ?? (await this.getDriverName(ride?.driverId)));
      if (dataStore.generalSettings.coDriverEnabled) {
        this.coDriverName.set(
          dayPartDay?.coDriverName ?? (await this.getDriverName(ride?.coDriverId)),
        );
      }
    });
  }

  onItemClick() {
    this.itemClicked.emit();
  }

  onMapsClick(event: Event) {
    event.stopPropagation();
    const ride = this.ride();
    if (ride) {
      this.openMaps(ride);
    }
  }

  openMaps(ride: Ride) {
    const mapsUrl = getMapsRouteUrl(ride);
    if (!mapsUrl) return;

    window.open(mapsUrl, '_blank');
  }

  formatTime(date: Date): string {
    date = valueToDate(date);
    if (!date) return '';
    return new Intl.DateTimeFormat('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  getTitle() {
    let title = '';
    const ride = this.ride();
    const vehicle = this.vehicle();
    if (ride && ride.availableSeats > 0) {
      title += `${ride.availableSeats} vrij, `;
    }
    if (vehicle && vehicle.name) {
      title += vehicle.name + ' ';
    }
    if (ride) {
      title += `${this.formatTime(ride.start)} - ${this.formatTime(ride.end)}`;
    }
    return title;
  }

  getPhoneNumber(user: UserInRide): string {
    if (!user) return '';

    return user.phoneNumber || user.phone || '';
  }

  getLocation(user: UserInRide): string {
    if (!user) return '';

    const from = typeof user.from === 'string' ? user.from : user.from?.address || '';
    const to = typeof user.to === 'string' ? user.to : user.to?.address || '';

    if (!from && !to) return '';
    if (!to) return from;
    if (!from) return to;

    return `${from} → ${to}`;
  }

  getVehicleColor(): string {
    return this.vehicle()?.calendarStyle?.color || '#ccc';
  }

  async getDriverName(driverId: string): Promise<string | null> {
    if (!driverId) {
      return null;
    }
    const driver = (
      await getDoc(doc(db, `organisations/${dataStore.selectedOrganisation.id}/users/${driverId}`))
    ).data() as OrgUser;
    return driver?.name || null;
  }
}
